import React, { useEffect, useRef, useState } from "react";
import financeV2Adapters from "../../../../adapters/financeV2Adapters";
import InfoTooltip from "../../FinanceV2/components/InfoTooltip";
import styles from "./EnveloppeCard.module.scss";

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Anime un nombre de sa valeur précédente vers `target` en requestAnimationFrame.
// À l'arrêt, renvoie exactement `target` (centimes préservés) ; pendant
// l'animation, renvoie une valeur intermédiaire à arrondir à l'entier.
export function useCountUp(target, duration = 900) {
  const [value, setValue] = useState(0);
  const fromRef = useRef(0);
  const rafRef = useRef();

  useEffect(() => {
    if (prefersReducedMotion()) {
      fromRef.current = target;
      setValue(target);
      return undefined;
    }
    const from = fromRef.current;
    const delta = target - from;
    if (delta === 0) {
      setValue(target);
      return undefined;
    }
    let startTs;
    const easeOut = (t) => 1 - Math.pow(1 - t, 3);
    const tick = (now) => {
      if (startTs === undefined) startTs = now;
      const p = Math.min((now - startTs) / duration, 1);
      if (p < 1) {
        setValue(from + delta * easeOut(p));
        rafRef.current = requestAnimationFrame(tick);
      } else {
        fromRef.current = target;
        setValue(target);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [target, duration]);

  return value;
}

// Formate un montant animé : valeur exacte (avec centimes) une fois posée,
// entier arrondi pendant le défilement pour un effet « compteur » propre.
export const formatMontant = (animated, target) =>
  animated === target
    ? Number(target).toLocaleString("fr-FR")
    : Math.round(animated).toLocaleString("fr-FR");

/**
 * Carte de capacité d'une enveloppe : barre de disponibilité + Réservé /
 * Engagé, avec compteurs animés.
 *
 * `consommation` = { financeur: {nom}, enveloppeOrigine, reserve, engage,
 *   disponible } (LS-601).
 *
 * Composant partagé : liste des demandes (EnveloppeDashboard) + gestion des
 * financeurs (FinanceurPanel).
 */
const EnveloppeCard = ({ consommation: c, index = 0 }) => {
  const origine = c.enveloppeOrigine;
  const consomme = c.reserve + c.engage;
  const ratio = financeV2Adapters.enveloppeRatio(consomme, origine);
  const color = financeV2Adapters.enveloppeColor(ratio);
  const colorText = financeV2Adapters.enveloppeColorText(ratio);
  // La jauge représente le DISPONIBLE (pleine = beaucoup de capacité).
  const dispoRatio = financeV2Adapters.enveloppeRatio(c.disponible, origine);
  const epuisee = origine <= 0;

  const restantTarget = Number(c.disponible);
  const reserveTarget = Number(c.reserve);
  const engageTarget = Number(c.engage);
  const origineTarget = Number(origine);

  const restant = useCountUp(restantTarget);
  const reserve = useCountUp(reserveTarget);
  const engage = useCountUp(engageTarget);
  const origineAnime = useCountUp(origineTarget);

  // Remplissage de la jauge : 0 au montage puis transition CSS vers la cible.
  const [barWidth, setBarWidth] = useState(0);
  useEffect(() => {
    if (prefersReducedMotion()) {
      setBarWidth(dispoRatio);
      return undefined;
    }
    const id = requestAnimationFrame(() => setBarWidth(dispoRatio));
    return () => cancelAnimationFrame(id);
  }, [dispoRatio]);

  return (
    <div
      className={styles.card}
      style={{
        borderLeftColor: epuisee ? "#dc3545" : color,
        animationDelay: `${index * 70}ms`,
      }}
    >
      <div className={styles.cardHead}>
        <span className={styles.cardName}>{c.financeur.nom}</span>
        <span className={styles.cardOrigine}>
          {formatMontant(origineAnime, origineTarget)} €
          <InfoTooltip text="Montant total alloué à cette enveloppe." />
        </span>
      </div>

      {epuisee ? (
        <div className={styles.cardEpuisee}>
          <i className="fa fa-exclamation-triangle" /> Enveloppe épuisée
        </div>
      ) : (
        <>
          <div className={styles.bar}>
            <div
              className={styles.barFill}
              style={{ width: `${barWidth}%`, background: color }}
            />
          </div>

          <div className={styles.restants}>
            <div className={styles.restantMain}>
              <span className={styles.restantLabel}>
                Disponible
                <InfoTooltip text="Capacité restante après déduction des demandes en cours et des demandes signées." />
              </span>
              <span className={styles.restantValue} style={{ color: colorText }}>
                {formatMontant(restant, restantTarget)} €
              </span>
            </div>
            <div className={styles.restantRow}>
              <span className={styles.restantLabel}>
                Réservé
                <InfoTooltip text="Somme des demandes en cours, du brouillon jusqu'au dossier transmis (non encore signées)." />
              </span>
              <span className={styles.restantSub}>
                {formatMontant(reserve, reserveTarget)} €
              </span>
            </div>
            <div className={styles.restantRow}>
              <span className={styles.restantLabel}>
                Engagé
                <InfoTooltip text="Somme des demandes transmises et signées — financement définitivement conclu." />
              </span>
              <span className={styles.restantSub}>
                {formatMontant(engage, engageTarget)} €
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EnveloppeCard;
